import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuthHeader } from 'react-auth-kit';
import { Modal, Container, Row, Col, ButtonToolbar, Button, Form, Card, Accordion, Badge, InputGroup } from 'react-bootstrap';
import * as Icon from 'react-feather';
import MercanciasForm from './MercanciasForm';
import { mexicoCityToday } from '../../../utils/dateTime';

const createEmptySatLookup = () => ({
    records: [],
    states: [],
    municipalities: [],
    localities: [],
    colonies: []
});

const calculateStayAmounts = (stay) => {
    const numeroDias = parseInt(stay.numero_dias || stay.cantidad || 0, 10) || 0;
    const valorUnitario = parseFloat(stay.valor_unitario || 0) || 0;
    const subtotal = numeroDias * valorUnitario;
    const iva = subtotal * 0.16;
    return {
        subtotal: subtotal.toFixed(2),
        iva: iva.toFixed(2),
        total: (subtotal + iva).toFixed(2)
    };
};

function DefaultModal(props) {
    const apiUrl = process.env.REACT_APP_URL;
    const authHeader = useAuthHeader();
    const today = mexicoCityToday();

    const [tripData, setTripData] = useState({
        client: '',
        route_id: '',
        invoice_id: '',
        cfditraslado: '',
        details: '',
        operator_id: '',
        inventory_id: '',
        operations_id: '',
        monitor_id: '',
        is_paid: false,
        company: ''
    });

    const [clients, setClients] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [units, setUnits] = useState([]);
    const [stops, setStops] = useState([]);
    const [maneuvers, setManeuvers] = useState([]);
    const [stays, setStays] = useState([]);
    const [mercancias, setMercancias] = useState([]);
    const [selectedTrailerId, setSelectedTrailerId] = useState('');
    const [loading, setLoading] = useState(false);
    const [operators, setOperators] = useState([]);
    const [monitors, setMonitors] = useState([]);
    const [satLookups, setSatLookups] = useState([]);

    // Load initial data when modal opens
    useEffect(() => {
        if (props.show) {
            loadClients();
            loadUnits();
            loadUsers();
            resetForm();
        }
    }, [props.show]);

    const resetForm = () => {
        setTripData({
            client: '',
            route_id: '',
            invoice_id: '',
            cfditraslado: '',
            details: '',
            operator_id: '',
            inventory_id: '',
            operations_id: '',
            monitor_id: '',
            is_paid: false,
            company: ''
        });
        setRoutes([]);
        setStops([]);
        setSatLookups([]);
        setMercancias([]);
        setStays([]);
        setSelectedTrailerId('');
    };

    const loadClients = async () => {
        try {
            const response = await axios.get(`${apiUrl}/finance/get_all_clients`, {
                headers: {
                    Authorization: authHeader()
                }
            });
            setClients(response.data.msg.Clients || []);
        } catch (error) {
            console.error('Error loading clients:', error);
            alert('Error al cargar clientes');
        }
    };

    const loadUnits = async () => {
        try {
            const response = await axios.get(`${apiUrl}/units/get_all_units`, {
                headers: {
                    Authorization: authHeader()
                }
            });
            setUnits(response.data.msg.Units || []);
        } catch (error) {
            console.error('Error loading units:', error);
            alert('Error al cargar unidades');
        }
    };

    const loadRoutesForClient = async (clientId) => {
        try {
            const response = await axios.get(`${apiUrl}/finance/get_routes?client_id=${clientId}`, {
                headers: {
                    Authorization: authHeader()
                }
            });
            setRoutes(response.data.msg.Routes || []);
        } catch (error) {
            console.error('Error loading routes:', error);
            alert('Error al cargar rutas');
        }
    };

    const loadUsers = async () => {
        try {
            const response = await axios.get(`${apiUrl}/users/get_all_users?user_type=all`, {
                headers: {
                    Authorization: authHeader()
                }
            });
            const users = response.data.msg.Users || [];

            // Filter users by role
            const operatorUsers = users.filter(user =>
                user.roles && user.roles.some(role => role.title.toLowerCase() === 'operador')
            );

            const monitorUsers = users.filter(user =>
                user.roles && user.roles.some(role => role.title.toLowerCase() === 'monitorista')
            );

            setOperators(operatorUsers);
            setMonitors(monitorUsers);
        } catch (error) {
            console.error('Error loading users:', error);
            alert('Error al cargar usuarios');
        }
    };

    const handleTripDataChange = (field, value) => {
        setTripData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const selectedUnit = units.find((unit) => String(unit.id) === String(tripData.inventory_id));
    const selectableUnits = units.filter((unit) => unit.equipment_role !== 'trailer');
    const trailerUnits = units.filter((unit) => unit.equipment_role === 'trailer');

    const handleClientChange = async (event) => {
        const clientId = event.target.value;
        const selectedOption = event.target.options[event.target.selectedIndex];
        const companyName = selectedOption.text;

        setTripData(prev => ({
            ...prev,
            client: clientId,
            company: companyName,
            route_id: '' // Reset route when client changes
        }));

        setRoutes([]);
        setStops([]);

        if (clientId) {
            await loadRoutesForClient(clientId);
        }
    };

    const handleRouteChange = async (event) => {
        const routeId = event.target.value;
        setTripData(prev => ({
            ...prev,
            route_id: routeId
        }));

        if (routeId) {
            try {
                const response = await axios.get(`${apiUrl}/finance/get_route_stops?route_id=${routeId}`, {
                    headers: {
                        Authorization: authHeader()
                    }
                });
                const routeStops = response.data.msg.Stops || [];

                // Initialize stops with default values
                const initializedStops = routeStops.map(routeStop => ({
                    ...routeStop,
                    origin_date: today,
                    origin_time: '08:00',
                    destination_date: today,
                    destination_time: '20:00',
                    load_type: 'Seco',
                    weight: 5,
                    status: 'Pendiente'
                }));

                setStops(initializedStops);
                setSatLookups(initializedStops.map(() => ({ origin: createEmptySatLookup(), destination: createEmptySatLookup() })));
                initializedStops.forEach((stop, index) => {
                    if (stop.origin_postal_code && stop.origin_postal_code.length === 5) {
                        lookupSatOptionsForPostalCode(index, 'origin', stop.origin_postal_code, stop.origin_address || '');
                    }
                    if (stop.destination_postal_code && stop.destination_postal_code.length === 5) {
                        lookupSatOptionsForPostalCode(index, 'destination', stop.destination_postal_code, stop.destination_address || '');
                    }
                });
            } catch (error) {
                console.error('Error loading route stops:', error);
                alert('Error al cargar paradas de la ruta');
            }
        }
    };

    const handleStopChange = (index, field, value) => {
        const updatedStops = [...stops];
        updatedStops[index] = {
            ...updatedStops[index],
            [field]: value
        };
        setStops(updatedStops);
    };

    const setSatLookupForStop = (index, prefix, lookup) => {
        setSatLookups(prev => {
            const next = [...prev];
            while (next.length <= index) {
                next.push({ origin: createEmptySatLookup(), destination: createEmptySatLookup() });
            }
            next[index] = {
                ...next[index],
                [prefix]: lookup
            };
            return next;
        });
    };

    const fetchSatLookup = async (postalCode, localityHint = '') => {
        const response = await axios.get(`${apiUrl}/finance/sat_location_options`, {
            params: {
                postal_code: postalCode,
                locality_hint: localityHint || '',
            },
            headers: {
                Authorization: authHeader()
            }
        });
        return response.data.msg || createEmptySatLookup();
    };

    const applySatDefaults = (index, prefix, lookup) => {
        setStops(prev => {
            const next = [...prev];
            const stop = { ...next[index] };
            const stateField = `${prefix}_estado`;
            const municipalityField = `${prefix}_municipio`;
            const localityField = `${prefix}_localidad`;
            const colonyField = `${prefix}_colonia`;

            const stateOptions = lookup.states || [];
            const municipalityOptions = lookup.municipalities || [];
            const localityOptions = lookup.localities || [];
            const colonyOptions = lookup.colonies || [];

            if ((!stop[stateField] || !stateOptions.some(option => option.code === stop[stateField])) && stateOptions.length === 1) {
                stop[stateField] = stateOptions[0].code;
            }
            if (!stop[municipalityField] || !municipalityOptions.some(option => option.code === stop[municipalityField])) {
                stop[municipalityField] = municipalityOptions.length === 1 ? municipalityOptions[0].code : '';
            }
            if (!stop[localityField] || !localityOptions.some(option => option.code === stop[localityField])) {
                if (lookup.suggested_locality_code && localityOptions.some(option => option.code === lookup.suggested_locality_code)) {
                    stop[localityField] = lookup.suggested_locality_code;
                } else if (localityOptions.length === 1) {
                    stop[localityField] = localityOptions[0].code;
                } else {
                    stop[localityField] = '';
                }
            }
            if (!stop[colonyField] || !colonyOptions.some(option => option.code === stop[colonyField])) {
                stop[colonyField] = colonyOptions.length === 1 ? colonyOptions[0].code : '';
            }

            next[index] = stop;
            return next;
        });
    };

    const lookupSatOptionsForPostalCode = async (index, prefix, postalCode, localityHint = '') => {
        if (!postalCode || postalCode.length !== 5) {
            setSatLookupForStop(index, prefix, createEmptySatLookup());
            return;
        }

        try {
            const lookup = await fetchSatLookup(postalCode, localityHint);
            setSatLookupForStop(index, prefix, lookup);
            applySatDefaults(index, prefix, lookup);
        } catch (error) {
            console.error('Error loading SAT location options:', error);
            setSatLookupForStop(index, prefix, createEmptySatLookup());
        }
    };

    const handlePostalCodeChange = (index, prefix, value) => {
        const normalizedValue = value.replace(/\D/g, '').slice(0, 5);
        handleStopChange(index, `${prefix}_postal_code`, normalizedValue);
        const currentStop = stops[index] || {};
        const localityHint = currentStop[`${prefix}_google_localidad`] || currentStop[`${prefix}_address`] || '';
        if (normalizedValue.length === 5) {
            lookupSatOptionsForPostalCode(index, prefix, normalizedValue, localityHint);
        } else {
            setSatLookupForStop(index, prefix, createEmptySatLookup());
        }
    };

    const buildOptionMap = (records, codeField, nameField) => {
        const map = new Map();
        records.forEach((record) => {
            const code = record[codeField];
            const name = record[nameField];
            if (!code && !name) return;
            if (!map.has(code)) {
                map.set(code, {
                    code,
                    name: name || '',
                    label: code && name ? `${code} - ${name}` : code || name || ''
                });
            }
        });
        return Array.from(map.values());
    };

    const getSatFieldOptions = (index, prefix) => {
        const lookup = satLookups[index]?.[prefix] || createEmptySatLookup();
        const records = lookup.records || [];
        const stop = stops[index] || {};
        const stateCode = stop[`${prefix}_estado`] || '';
        const municipalityCode = stop[`${prefix}_municipio`] || '';
        const localityCode = stop[`${prefix}_localidad`] || '';

        const municipalityRecords = stateCode
            ? records.filter(record => record.state_code === stateCode)
            : records;
        const localityRecords = stateCode || municipalityCode
            ? records.filter(record =>
                (!stateCode || record.state_code === stateCode) &&
                (!municipalityCode || record.municipality_code === municipalityCode)
            )
            : records;
        const colonyRecords = stateCode || municipalityCode || localityCode
            ? records.filter(record =>
                (!stateCode || record.state_code === stateCode) &&
                (!municipalityCode || record.municipality_code === municipalityCode) &&
                (!localityCode || record.locality_code === localityCode)
            )
            : records;

        const stateOptions = buildOptionMap(records, 'state_code', 'state_name');
        const municipalityOptions = buildOptionMap(municipalityRecords, 'municipality_code', 'municipality_name');
        const localityOptions = buildOptionMap(localityRecords, 'locality_code', 'locality_name');
        const colonyOptions = buildOptionMap(colonyRecords, 'colony_code', 'colony_name');

        return {
            states: stateOptions.length ? stateOptions : (lookup.states || []),
            municipalities: municipalityOptions.length ? municipalityOptions : (lookup.municipalities || []),
            localities: localityOptions.length ? localityOptions : (lookup.localities || []),
            colonies: colonyOptions.length ? colonyOptions : (lookup.colonies || []),
        };
    };

    const addStop = () => {
        const newStop = {
            origin_address: '',
            destination_address: '',
            origin_postal_code: '',
            destination_postal_code: '',
            origin_rfc: '',
            destination_rfc: '',
            origin_estado: '',
            destination_estado: '',
            origin_municipio: '',
            destination_municipio: '',
            origin_localidad: '',
            destination_localidad: '',
            origin_colonia: '',
            destination_colonia: '',
            origin_calle: '',
            destination_calle: '',
            origin_date: today,
            origin_time: '08:00',
            destination_date: today,
            destination_time: '20:00',
            load_type: 'Seco',
            weight: 5,
            status: 'Pendiente'
        };
        setStops([...stops, newStop]);
        setSatLookups([...satLookups, { origin: createEmptySatLookup(), destination: createEmptySatLookup() }]);
    };

    const removeStop = (index) => {
        const updatedStops = stops.filter((_, i) => i !== index);
        setStops(updatedStops);
        setSatLookups(satLookups.filter((_, i) => i !== index));
    };

    // Maneuver Functions
    const addManeuver = () => {
        const newManeuver = {
            invoice_id: '',
            subtotal: 0,
            iva: 0,
            total: 0,
            performed_by_trip_operator: false,
            performer_user_id: null,
            amount_paid: 0,
            description: ''
        };
        setManeuvers([...maneuvers, newManeuver]);
    };

    const removeManeuver = (index) => {
        const updatedManeuvers = maneuvers.filter((_, i) => i !== index);
        setManeuvers(updatedManeuvers);
    };

    const handleManeuverChange = (index, field, value) => {
        const updatedManeuvers = [...maneuvers];
        updatedManeuvers[index] = {
            ...updatedManeuvers[index],
            [field]: value
        };

        // Auto-calculate IVA and total when subtotal changes
        if (field === 'subtotal') {
            const subtotal = parseFloat(value) || 0;
            updatedManeuvers[index].iva = (subtotal * 0.16).toFixed(2);
            updatedManeuvers[index].total = (subtotal + (subtotal * 0.16)).toFixed(2);
        }

        // Set performer_user_id based on checkbox state
        if (field === 'performed_by_trip_operator') {
            if (value === true) {
                // Set to operator_id when performed by trip operator
                updatedManeuvers[index].performer_user_id = tripData.operator_id || null;
            } else {
                // Set to null when not performed by trip operator
                updatedManeuvers[index].performer_user_id = null;
            }
        }

        setManeuvers(updatedManeuvers);
    };

    // Stay Functions
    const addStay = () => {
        const newStay = {
            invoice_id: '',
            numero_dias: 1,
            valor_unitario: 0,
            subtotal: 0,
            iva: 0,
            total: 0,
            description: ''
        };
        setStays([...stays, newStay]);
    };

    const removeStay = (index) => {
        setStays(stays.filter((_, i) => i !== index));
    };

    const handleStayChange = (index, field, value) => {
        const updated = [...stays];
        if (field === 'numero_dias') {
            value = value === '' ? '' : String(Math.max(1, parseInt(value, 10) || 1));
        }
        updated[index] = { ...updated[index], [field]: value };

        if (field === 'numero_dias' || field === 'valor_unitario') {
            updated[index] = {
                ...updated[index],
                ...calculateStayAmounts(updated[index])
            };
        }

        setStays(updated);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!tripData.operator_id) {
            alert('Selecciona un operador antes de guardar el viaje.');
            return;
        }

        if (!tripData.inventory_id) {
            alert('Selecciona una unidad antes de guardar el viaje.');
            return;
        }

        if (selectedUnit?.requires_trailer && !selectedTrailerId) {
            alert('Selecciona un remolque antes de guardar el viaje.');
            return;
        }

        setLoading(true);

        try {
            const trailers = selectedTrailerId
                ? [{ trailer_inventory_id: selectedTrailerId, position: 1 }]
                : [];
            const payload = {
                ...tripData,
                stops: stops,
                maneuvers: maneuvers,
                mercancias: mercancias,
                stays: stays,
                trailers: trailers
            };

            await axios.post(`${apiUrl}/trips/add_trip`, payload, {
                headers: {
                    Authorization: authHeader()
                }
            });

            props.setData();
            props.onHide();
            resetForm();
        } catch (error) {
            console.error('Error creating trip:', error);
            const message = error.response?.data?.error || 'Error al crear el viaje';
            alert(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal {...props} size="xl" aria-labelledby="contained-modal-title-vcenter">
            <Modal.Header closeButton>
                <Modal.Title id="contained-modal-title-vcenter">
                    Agregar Viaje
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <Container>
                    <Form onSubmit={handleSubmit}>
                        {/* Basic Trip Information */}
                        <Card className="mb-3">
                            <Card.Header>
                                <h6 className="mb-0">Información Básica del Viaje</h6>
                            </Card.Header>
                            <Card.Body>
                                <Row className="mb-3">
                                    <Form.Group as={Col} md={4}>
                                        <Form.Label>Cliente *</Form.Label>
                                        <Form.Select
                                            value={tripData.client}
                                            onChange={handleClientChange}
                                            required
                                        >
                                            <option value="">Seleccionar cliente...</option>
                                            {clients.map((client) => (
                                                <option key={client.id} value={client.id}>
                                                    {client.company_name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group as={Col} md={4}>
                                        <Form.Label>Ruta</Form.Label>
                                        <Form.Select
                                            value={tripData.route_id}
                                            onChange={handleRouteChange}
                                        >
                                            <option value="">Seleccionar ruta...</option>
                                            {routes.map((route) => (
                                                <option key={route.id} value={route.id}>
                                                    {route.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group as={Col} md={4}>
                                        <Form.Label>No. Factura Cobro</Form.Label>
                                        <Form.Control
                                            value={tripData.invoice_id}
                                            onChange={(e) => handleTripDataChange('invoice_id', e.target.value)}
                                        />
                                    </Form.Group>
                                </Row>

                                <Row className="mb-3">
                                    <Form.Group as={Col} md={4}>
                                        <Form.Label>CFDI Traslado</Form.Label>
                                        <Form.Control
                                            value={tripData.cfditraslado}
                                            onChange={(e) => handleTripDataChange('cfditraslado', e.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col} md={4}>
                                        <Form.Label>Operador *</Form.Label>
                                        <Form.Select
                                            value={tripData.operator_id}
                                            onChange={(e) => handleTripDataChange('operator_id', e.target.value)}
                                            required
                                        >
                                            <option value="">Seleccionar operador...</option>
                                            {operators.map((user) => (
                                                <option key={user.id} value={user.id}>
                                                    {user.first_name} {user.last_name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group as={Col} md={4}>
                                        <Form.Label>Unidad *</Form.Label>
                                        <Form.Select
                                            value={tripData.inventory_id}
                                            onChange={(e) => {
                                                handleTripDataChange('inventory_id', e.target.value);
                                                setSelectedTrailerId('');
                                            }}
                                            required
                                        >
                                            <option value="">Seleccionar unidad...</option>
                                            {selectableUnits.map((unit) => (
                                                <option key={unit.id} value={unit.id}>
                                                    {unit.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Row>

                                {selectedUnit?.requires_trailer && (
                                    <Row className="mb-3">
                                        <Form.Group as={Col} md={4}>
                                            <Form.Label>Remolque *</Form.Label>
                                            <Form.Select
                                                value={selectedTrailerId}
                                                onChange={(e) => setSelectedTrailerId(e.target.value)}
                                                required
                                            >
                                                <option value="">Seleccionar remolque...</option>
                                                {trailerUnits.map((unit) => (
                                                    <option key={unit.id} value={unit.id}>
                                                        {unit.name} {unit.plates ? `- ${unit.plates}` : ''}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Row>
                                )}

                                <Row className="mb-3">
                                    <Form.Group as={Col} md={4}>
                                        <Form.Label>Operaciones</Form.Label>
                                        <Form.Select
                                            value={tripData.operations_id}
                                            onChange={(e) => handleTripDataChange('operations_id', e.target.value)}
                                        >
                                            <option value="">Seleccionar...</option>
                                            {monitors.map((user) => (
                                                <option key={user.id} value={user.id}>
                                                    {user.first_name} {user.last_name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group as={Col} md={4}>
                                        <Form.Label>Monitorista</Form.Label>
                                        <Form.Select
                                            value={tripData.monitor_id}
                                            onChange={(e) => handleTripDataChange('monitor_id', e.target.value)}
                                        >
                                            <option value="">Seleccionar...</option>
                                            {monitors.map((user) => (
                                                <option key={user.id} value={user.id}>
                                                    {user.first_name} {user.last_name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group as={Col} md={4}>
                                        <Form.Check
                                            type="checkbox"
                                            label="Pagado"
                                            checked={tripData.is_paid}
                                            onChange={(e) => handleTripDataChange('is_paid', e.target.checked)}
                                            className="mt-4"
                                        />
                                    </Form.Group>
                                </Row>

                                <Row>
                                    <Form.Group as={Col}>
                                        <Form.Label>Tipo de Carga</Form.Label>
                                        <Form.Control
                                            value={tripData.details}
                                            onChange={(e) => handleTripDataChange('details', e.target.value)}
                                            placeholder="Pimientos secos"
                                        />
                                    </Form.Group>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Trip Stops */}
                        <Card className="mb-3">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-0">Paradas del Viaje</h6>
                                <Button
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={addStop}
                                    type="button"
                                >
                                    <Icon.Plus size={16} /> Agregar Parada
                                </Button>
                            </Card.Header>
                            <Card.Body>
                                {stops.length > 0 && (
                                    <Accordion>
                                        {stops.map((stop, index) => (
                                            <Accordion.Item eventKey={index.toString()} key={index}>
                                                <Accordion.Header>
                                                    <div className="d-flex justify-content-between w-100 me-3">
                                                        <span>
                                                            Parada {index + 1}: {stop.origin_address || 'Nueva parada'} → {stop.destination_address || 'Destino'}
                                                        </span>
                                                        <div className="d-flex gap-2">
                                                            {/* <Badge bg="secondary">{stop.status || 'Pendiente'}</Badge> */}
                                                            {stops.length > 1 && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline-danger"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        removeStop(index);
                                                                    }}
                                                                    type="button"
                                                                >
                                                                    <Icon.X size={12} />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </Accordion.Header>
                                                <Accordion.Body>
                                                    {/* Trip Route Card */}
                                                    <div className="mb-4">
                                                        <Card className="border-0 bg-light">
                                                            <Card.Body className="py-3">
                                                                <Row className="align-items-center">
                                                                    <Col md={4} className="text-center">
                                                                        <div className="mb-2">
                                                                            <h6 className="mb-1 text-primary">Origen</h6>
                                                                            <div className="fw-bold fs-5">{stop.origin_date || today}</div>
                                                                            <div className="text-muted small">{stop.origin_time || '08:00'}</div>
                                                                        </div>
                                                                        {stop.origin_postal_code && (
                                                                            <Badge bg="secondary" className="mb-1">C.P. {stop.origin_postal_code}</Badge>
                                                                        )}
                                                                        {stop.origin_rfc && (
                                                                            <Badge bg="dark" className="mb-1 ms-1">RFC: {stop.origin_rfc}</Badge>
                                                                        )}
                                                                        <Form.Control
                                                                            value={stop.origin_address || ''}
                                                                            onChange={(e) => handleStopChange(index, 'origin_address', e.target.value)}
                                                                            placeholder="Dirección de origen"
                                                                            className="text-center border-0 bg-transparent fw-semibold"
                                                                            style={{ fontSize: '0.9rem' }}
                                                                        />
                                                                    </Col>

                                                                    <Col md={4} className="text-center">
                                                                        <div className="d-flex align-items-center justify-content-center">
                                                                            <div className="border-top border-2 border-primary flex-grow-1"></div>
                                                                            <div className="mx-3">
                                                                                <Icon.Truck size={24} className="text-primary" />
                                                                            </div>
                                                                            <div className="border-top border-2 border-primary flex-grow-1"></div>
                                                                        </div>
                                                                        {stop.distance_km && (
                                                                            <div className="mt-1">
                                                                                <Badge bg="info">{stop.distance_km} km</Badge>
                                                                            </div>
                                                                        )}
                                                                        <div className="mt-2">
                                                                            <Badge bg={stop.status === 'Entregado' ? 'success' : stop.status === 'En Camino' ? 'info' : stop.status === 'Cancelado' ? 'danger' : 'warning'}>
                                                                                {stop.status || 'Pendiente'}
                                                                            </Badge>
                                                                        </div>
                                                                    </Col>

                                                                    <Col md={4} className="text-center">
                                                                        <div className="mb-2">
                                                                            <h6 className="mb-1 text-success">Destino</h6>
                                                                            <div className="fw-bold fs-5">{stop.destination_date || today}</div>
                                                                            <div className="text-muted small">{stop.destination_time || '20:00'}</div>
                                                                        </div>
                                                                        {stop.destination_postal_code && (
                                                                            <Badge bg="secondary" className="mb-1">C.P. {stop.destination_postal_code}</Badge>
                                                                        )}
                                                                        {stop.destination_rfc && (
                                                                            <Badge bg="dark" className="mb-1 ms-1">RFC: {stop.destination_rfc}</Badge>
                                                                        )}
                                                                        <Form.Control
                                                                            value={stop.destination_address || ''}
                                                                            onChange={(e) => handleStopChange(index, 'destination_address', e.target.value)}
                                                                            placeholder="Dirección de destino"
                                                                            className="text-center border-0 bg-transparent fw-semibold"
                                                                            style={{ fontSize: '0.9rem' }}
                                                                        />
                                                                    </Col>
                                                                </Row>
                                                            </Card.Body>
                                                        </Card>
                                                    </div>

                                                    {/* Date and Time Controls */}
                                                    <Row className="mb-3">
                                                        <Col md={6}>
                                                            <h6 className="text-primary mb-2">
                                                                <Icon.MapPin size={16} className="me-1" />
                                                                Horarios de Carga
                                                            </h6>
                                                            <Row>
                                                                <Form.Group as={Col} md={6}>
                                                                    <Form.Label className="small">Fecha</Form.Label>
                                                                    <Form.Control
                                                                        type="date"
                                                                        value={stop.origin_date || today}
                                                                        onChange={(e) => handleStopChange(index, 'origin_date', e.target.value)}
                                                                    />
                                                                </Form.Group>
                                                                <Form.Group as={Col} md={6}>
                                                                    <Form.Label className="small">Hora</Form.Label>
                                                                    <Form.Control
                                                                        type="time"
                                                                        value={stop.origin_time || '08:00'}
                                                                        onChange={(e) => handleStopChange(index, 'origin_time', e.target.value)}
                                                                    />
                                                                </Form.Group>
                                                            </Row>
                                                        </Col>
                                                        <Col md={6}>
                                                            <h6 className="text-success mb-2">
                                                                <Icon.Flag size={16} className="me-1" />
                                                                Horarios de Descarga
                                                            </h6>
                                                            <Row>
                                                                <Form.Group as={Col} md={6}>
                                                                    <Form.Label className="small">Fecha</Form.Label>
                                                                    <Form.Control
                                                                        type="date"
                                                                        value={stop.destination_date || today}
                                                                        onChange={(e) => handleStopChange(index, 'destination_date', e.target.value)}
                                                                    />
                                                                </Form.Group>
                                                                <Form.Group as={Col} md={6}>
                                                                    <Form.Label className="small">Hora</Form.Label>
                                                                    <Form.Control
                                                                        type="time"
                                                                        value={stop.destination_time || '20:00'}
                                                                        onChange={(e) => handleStopChange(index, 'destination_time', e.target.value)}
                                                                    />
                                                                </Form.Group>
                                                            </Row>
                                                        </Col>
                                                    </Row>

                                                    {/* Additional Details */}
                                                <Row className="mb-3">
                                                    <Form.Group as={Col} md={3}>
                                                        <Form.Label>Tipo de Carga</Form.Label>
                                                            <Form.Select
                                                                value={stop.load_type || 'Seco'}
                                                                onChange={(e) => handleStopChange(index, 'load_type', e.target.value)}
                                                            >
                                                                <option value="Seco">Seco</option>
                                                                <option value="Comida">Comida</option>
                                                                <option value="Otro">Otro</option>
                                                            </Form.Select>
                                                        </Form.Group>
                                                        <Form.Group as={Col} md={3}>
                                                            <Form.Label>Peso (ton)</Form.Label>
                                                            <Form.Control
                                                                type="number"
                                                                min="1"
                                                                max="50"
                                                                step="0.1"
                                                                value={stop.weight || 5}
                                                                onChange={(e) => handleStopChange(index, 'weight', e.target.value)}
                                                            />
                                                        </Form.Group>
                                                        <Form.Group as={Col} md={6}>
                                                            <Form.Label>Estado</Form.Label>
                                                            <Form.Select
                                                                value={stop.status || 'Pendiente'}
                                                                onChange={(e) => handleStopChange(index, 'status', e.target.value)}
                                                            >
                                                                <option value="Pendiente">Pendiente</option>
                                                                <option value="En Camino">En Camino</option>
                                                                <option value="Entregado">Entregado</option>
                                                                <option value="Cancelado">Cancelado</option>
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </Row>

                                                    <Row className="mb-3">
                                                        <Form.Group as={Col} md={3}>
                                                            <Form.Label>C.P. Origen</Form.Label>
                                                            <Form.Control
                                                                value={stop.origin_postal_code || ''}
                                                                onChange={(e) => handlePostalCodeChange(index, 'origin', e.target.value)}
                                                                placeholder="Ej: 44100"
                                                                maxLength={5}
                                                            />
                                                        </Form.Group>
                                                        <Form.Group as={Col} md={3}>
                                                            <Form.Label>RFC Origen</Form.Label>
                                                            <Form.Control
                                                                value={stop.origin_rfc || ''}
                                                                onChange={(e) => handleStopChange(index, 'origin_rfc', e.target.value.toUpperCase())}
                                                                placeholder="Ej: XAXX010101000"
                                                                maxLength={13}
                                                            />
                                                        </Form.Group>
                                                        <Form.Group as={Col} md={3}>
                                                            <Form.Label>C.P. Destino</Form.Label>
                                                            <Form.Control
                                                                value={stop.destination_postal_code || ''}
                                                                onChange={(e) => handlePostalCodeChange(index, 'destination', e.target.value)}
                                                                placeholder="Ej: 78490"
                                                                maxLength={5}
                                                            />
                                                        </Form.Group>
                                                        <Form.Group as={Col} md={3}>
                                                            <Form.Label>RFC Destino</Form.Label>
                                                            <Form.Control
                                                                value={stop.destination_rfc || ''}
                                                                onChange={(e) => handleStopChange(index, 'destination_rfc', e.target.value.toUpperCase())}
                                                                placeholder="Ej: XAXX010101000"
                                                                maxLength={13}
                                                            />
                                                        </Form.Group>
                                                    </Row>

                                                    <Row className="mb-2">
                                                        <Col>
                                                            <h6 className="text-muted mb-2">Codigos SAT para Carta Porte</h6>
                                                            <div className="small text-muted">
                                                                Conservamos la direccion legible de Google arriba. Aqui van los codigos SAT, no los nombres de ciudad o estado.
                                                            </div>
                                                        </Col>
                                                    </Row>

                                                    <Row className="mb-3">
                                                        <Form.Group as={Col} md={4}>
                                                            <Form.Label>Estado SAT Origen</Form.Label>
                                                            <Form.Select
                                                                value={stop.origin_estado || ''}
                                                                onChange={(e) => handleStopChange(index, 'origin_estado', e.target.value.toUpperCase())}
                                                            >
                                                                <option value="">Seleccionar estado SAT...</option>
                                                                {getSatFieldOptions(index, 'origin').states.map((option) => (
                                                                    <option key={`origin-state-${option.code}`} value={option.code}>
                                                                        {option.label}
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                        </Form.Group>
                                                        <Form.Group as={Col} md={4}>
                                                            <Form.Label>Municipio SAT Origen</Form.Label>
                                                            <Form.Select
                                                                value={stop.origin_municipio || ''}
                                                                onChange={(e) => handleStopChange(index, 'origin_municipio', e.target.value)}
                                                            >
                                                                <option value="">Seleccionar municipio SAT...</option>
                                                                {getSatFieldOptions(index, 'origin').municipalities.map((option) => (
                                                                    <option key={`origin-municipality-${option.code}`} value={option.code}>
                                                                        {option.label}
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                        </Form.Group>
                                                        <Form.Group as={Col} md={4}>
                                                            <Form.Label>Localidad SAT Origen</Form.Label>
                                                            <Form.Select
                                                                value={stop.origin_localidad || ''}
                                                                onChange={(e) => handleStopChange(index, 'origin_localidad', e.target.value)}
                                                            >
                                                                <option value="">Seleccionar localidad SAT...</option>
                                                                {getSatFieldOptions(index, 'origin').localities.map((option) => (
                                                                    <option key={`origin-locality-${option.code}`} value={option.code}>
                                                                        {option.label}
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </Row>

                                                    <Row className="mb-3">
                                                        <Form.Group as={Col} md={4}>
                                                            <Form.Label>Colonia SAT Origen</Form.Label>
                                                            <Form.Select
                                                                value={stop.origin_colonia || ''}
                                                                onChange={(e) => handleStopChange(index, 'origin_colonia', e.target.value)}
                                                            >
                                                                <option value="">Seleccionar colonia SAT...</option>
                                                                {getSatFieldOptions(index, 'origin').colonies.map((option) => (
                                                                    <option key={`origin-colony-${option.code}`} value={option.code}>
                                                                        {option.label}
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </Row>

                                                    <Row className="mb-3">
                                                        <Form.Group as={Col} md={4}>
                                                            <Form.Label>Estado SAT Destino</Form.Label>
                                                            <Form.Select
                                                                value={stop.destination_estado || ''}
                                                                onChange={(e) => handleStopChange(index, 'destination_estado', e.target.value.toUpperCase())}
                                                            >
                                                                <option value="">Seleccionar estado SAT...</option>
                                                                {getSatFieldOptions(index, 'destination').states.map((option) => (
                                                                    <option key={`destination-state-${option.code}`} value={option.code}>
                                                                        {option.label}
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                        </Form.Group>
                                                        <Form.Group as={Col} md={4}>
                                                            <Form.Label>Municipio SAT Destino</Form.Label>
                                                            <Form.Select
                                                                value={stop.destination_municipio || ''}
                                                                onChange={(e) => handleStopChange(index, 'destination_municipio', e.target.value)}
                                                            >
                                                                <option value="">Seleccionar municipio SAT...</option>
                                                                {getSatFieldOptions(index, 'destination').municipalities.map((option) => (
                                                                    <option key={`destination-municipality-${option.code}`} value={option.code}>
                                                                        {option.label}
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                        </Form.Group>
                                                        <Form.Group as={Col} md={4}>
                                                            <Form.Label>Localidad SAT Destino</Form.Label>
                                                            <Form.Select
                                                                value={stop.destination_localidad || ''}
                                                                onChange={(e) => handleStopChange(index, 'destination_localidad', e.target.value)}
                                                            >
                                                                <option value="">Seleccionar localidad SAT...</option>
                                                                {getSatFieldOptions(index, 'destination').localities.map((option) => (
                                                                    <option key={`destination-locality-${option.code}`} value={option.code}>
                                                                        {option.label}
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </Row>

                                                    <Row className="mb-3">
                                                        <Form.Group as={Col} md={4}>
                                                            <Form.Label>Colonia SAT Destino</Form.Label>
                                                            <Form.Select
                                                                value={stop.destination_colonia || ''}
                                                                onChange={(e) => handleStopChange(index, 'destination_colonia', e.target.value)}
                                                            >
                                                                <option value="">Seleccionar colonia SAT...</option>
                                                                {getSatFieldOptions(index, 'destination').colonies.map((option) => (
                                                                    <option key={`destination-colony-${option.code}`} value={option.code}>
                                                                        {option.label}
                                                                    </option>
                                                                ))}
                                                            </Form.Select>
                                                        </Form.Group>
                                                    </Row>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        ))}
                                    </Accordion>
                                )}
                                {stops.length === 0 && (
                                    <div className="text-center text-muted py-4">
                                        <p>Selecciona una ruta para cargar las paradas automáticamente</p>
                                        <p>o agrega paradas manualmente usando el botón de arriba</p>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>

                        {/* Mercancias */}
                        <MercanciasForm mercancias={mercancias} setMercancias={setMercancias} stops={stops} />

                        {/* Maneuvers Section */}
                        <Card className="mb-3">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-0">Maniobras</h6>
                                <Button
                                    size="sm"
                                    variant="outline-success"
                                    onClick={addManeuver}
                                    type="button"
                                >
                                    <Icon.Plus size={16} /> Agregar Maniobra
                                </Button>
                            </Card.Header>
                            <Card.Body>
                                {maneuvers.length > 0 && (
                                    <Accordion>
                                        {maneuvers.map((maneuver, index) => (
                                            <Accordion.Item eventKey={`maneuver-${index}`} key={index}>
                                                <Accordion.Header>
                                                    <div className="d-flex justify-content-between w-100 me-3">
                                                        <span>
                                                            <Icon.Tool size={16} className="me-2" />
                                                            Maniobra {index + 1}: {maneuver.invoice_id || 'Nueva Maniobra'}
                                                        </span>
                                                        <div className="d-flex gap-2 align-items-center">
                                                            <Badge bg="primary">${parseFloat(maneuver.total || 0).toFixed(2)}</Badge>
                                                            <Button
                                                                size="sm"
                                                                variant="outline-danger"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    removeManeuver(index);
                                                                }}
                                                                type="button"
                                                            >
                                                                <Icon.X size={12} />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </Accordion.Header>
                                                <Accordion.Body>
                                                    <Row className="mb-3">
                                                        <Col md={6}>
                                                            <h6 className="text-primary mb-3">Información de Facturación</h6>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>Factura</Form.Label>
                                                                <InputGroup>
                                                                    <Form.Control
                                                                        value={maneuver.invoice_id || ''}
                                                                        placeholder="Número de factura"
                                                                        readOnly
                                                                        disabled
                                                                    />
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline-secondary"
                                                                        disabled
                                                                        title="Guarda el viaje para generar la factura"
                                                                    >
                                                                        <Icon.DollarSign size={16} />
                                                                    </Button>
                                                                </InputGroup>
                                                            </Form.Group>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>Subtotal</Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={maneuver.subtotal || 0}
                                                                    onChange={(e) => handleManeuverChange(index, 'subtotal', e.target.value)}
                                                                    placeholder="0.00"
                                                                />
                                                            </Form.Group>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>IVA (16%)</Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={maneuver.iva || 0}
                                                                    readOnly
                                                                    disabled
                                                                />
                                                            </Form.Group>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-success fw-bold">Total</Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={maneuver.total || 0}
                                                                    readOnly
                                                                    disabled
                                                                    className="fw-bold"
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={6}>
                                                            <h6 className="text-info mb-3">Información del Ejecutor</h6>
                                                            <Form.Group className="mb-3">
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    label="Realizado por el operador del viaje"
                                                                    checked={maneuver.performed_by_trip_operator}
                                                                    onChange={(e) => handleManeuverChange(index, 'performed_by_trip_operator', e.target.checked)}
                                                                />
                                                            </Form.Group>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>Monto a pagar</Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={maneuver.amount_paid || 0}
                                                                    onChange={(e) => handleManeuverChange(index, 'amount_paid', e.target.value)}
                                                                    placeholder="0.00"
                                                                />
                                                            </Form.Group>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>Descripción</Form.Label>
                                                                <Form.Control
                                                                    as="textarea"
                                                                    rows={3}
                                                                    value={maneuver.description || ''}
                                                                    onChange={(e) => handleManeuverChange(index, 'description', e.target.value)}
                                                                    placeholder="Descripción de la maniobra"
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        ))}
                                    </Accordion>
                                )}
                                {maneuvers.length === 0 && (
                                    <div className="text-center text-muted py-4">
                                        <Icon.Tool size={32} className="mb-2" />
                                        <p>No hay maniobras agregadas</p>
                                        <p className="small">Usa el botón de arriba para agregar una maniobra</p>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>

                        {/* Estadias Section */}
                        <Card className="mb-3">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                                <h6 className="mb-0">Estadias</h6>
                                <Button
                                    size="sm"
                                    variant="outline-info"
                                    onClick={addStay}
                                    type="button"
                                >
                                    <Icon.Plus size={16} /> Agregar Estadia
                                </Button>
                            </Card.Header>
                            <Card.Body>
                                {stays.length > 0 && (
                                    <Accordion>
                                        {stays.map((stay, index) => (
                                            <Accordion.Item eventKey={`stay-${index}`} key={index}>
                                                <Accordion.Header>
                                                    <div className="d-flex justify-content-between w-100 me-3">
                                                        <span>
                                                            <Icon.Clock size={16} className="me-2" />
                                                            Estadia {index + 1}: {stay.invoice_id || 'Nueva Estadia'}
                                                        </span>
                                                        <div className="d-flex gap-2 align-items-center">
                                                            <Badge bg="info">${parseFloat(stay.total || 0).toFixed(2)}</Badge>
                                                            <Button
                                                                size="sm"
                                                                variant="outline-danger"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    removeStay(index);
                                                                }}
                                                                type="button"
                                                            >
                                                                <Icon.X size={12} />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </Accordion.Header>
                                                <Accordion.Body>
                                                    <Row className="mb-3">
                                                        <Col md={6}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>Factura</Form.Label>
                                                                <InputGroup>
                                                                    <Form.Control
                                                                        value={stay.invoice_id || ''}
                                                                        placeholder="Número de factura"
                                                                        readOnly
                                                                        disabled
                                                                    />
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline-secondary"
                                                                        disabled
                                                                        title="Guarda el viaje para generar la factura"
                                                                    >
                                                                        <Icon.DollarSign size={16} />
                                                                    </Button>
                                                                </InputGroup>
                                                            </Form.Group>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>Número de días</Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="1"
                                                                    min="1"
                                                                    value={stay.numero_dias || ''}
                                                                    onChange={(e) => handleStayChange(index, 'numero_dias', e.target.value)}
                                                                    placeholder="Cantidad"
                                                                />
                                                            </Form.Group>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>Valor unitario</Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    min="0"
                                                                    value={stay.valor_unitario || ''}
                                                                    onChange={(e) => handleStayChange(index, 'valor_unitario', e.target.value)}
                                                                    placeholder="0.00"
                                                                />
                                                            </Form.Group>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>Importe</Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={stay.subtotal || 0}
                                                                    readOnly
                                                                    disabled
                                                                />
                                                            </Form.Group>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>IVA (16%)</Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={stay.iva || 0}
                                                                    readOnly
                                                                    disabled
                                                                />
                                                            </Form.Group>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label className="text-success fw-bold">Total</Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={stay.total || 0}
                                                                    readOnly
                                                                    disabled
                                                                    className="fw-bold"
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={6}>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>Descripcion</Form.Label>
                                                                <Form.Control
                                                                    as="textarea"
                                                                    rows={5}
                                                                    value={stay.description || ''}
                                                                    onChange={(e) => handleStayChange(index, 'description', e.target.value)}
                                                                    placeholder="Descripcion de la estadia"
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                </Accordion.Body>
                                            </Accordion.Item>
                                        ))}
                                    </Accordion>
                                )}
                                {stays.length === 0 && (
                                    <div className="text-center text-muted py-4">
                                        <Icon.Clock size={32} className="mb-2" />
                                        <p>No hay estadias agregadas</p>
                                        <p className="small">Usa el boton de arriba para agregar una estadia</p>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>

                        <div className="text-end">
                            <Button variant="secondary" onClick={props.onHide} className="me-2" type="button">
                                Cancelar
                            </Button>
                            <Button type="submit" variant="primary" disabled={loading}>
                                {loading ? 'Guardando...' : 'Guardar Viaje'}
                            </Button>
                        </div>
                    </Form>
                </Container>
            </Modal.Body>
        </Modal>
    );
}

class ModalWithGrid extends React.Component {
    constructor(...args) {
        super(...args);
        this.state = { modalShow: false };
    }

    render() {
        let add_new_trip = () => {
            this.props.setData();
            this.setState({ modalShow: false });
        }

        return (
            <ButtonToolbar>
                <Button
                    variant="primary"
                    onClick={() => this.setState({ modalShow: true })}
                >
                    Agregar Viaje
                </Button>
                <DefaultModal
                    show={this.state.modalShow}
                    onHide={add_new_trip}
                    setData={this.props.setData}
                />
            </ButtonToolbar>
        );
    }
}

export default ModalWithGrid;
